import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { CaseSummary } from '../api/types'
import { CorrectnessBadge } from '../components/CorrectnessBadge'
import { useViewMode } from '../components/ModeProvider'
import { Term } from '../components/Term'
import { primeCaseIndex } from '../lib/case-index'
import {
  buildPredictionView,
  correctness,
  VERDICT_LABEL,
} from '../lib/predict'

const ROLE_LABEL: Record<string, string> = {
  clean_malignant_ambl: 'Belirgin kötü huylu (AMBL)',
  clean_benign_ambl: 'Belirgin iyi huylu (AMBL)',
  borderline_correct_malignant: 'Sınır vaka, kötü huylu',
  borderline_correct_benign: 'Sınır vaka, iyi huylu',
  false_negative_risk: 'Yanlış negatif riski',
  false_positive_risk: 'Yanlış pozitif riski',
  multifocal_ambl: 'Çok odaklı lezyon (AMBL)',
  calibration_sweet_spot: 'Kalibrasyonu iyi örnek',
  model_disagreement: 'Modeller çelişiyor',
  bd_malignant_domain_shift: 'Kohort kayması, kötü huylu (BD)',
  bd_ensemble_disagreement: 'BD topluluk çelişkisi',
  bd_benign: 'İyi huylu lezyon (BD)',
  diversity_filler: 'Çeşitlilik için seçildi',
  borderline_ambl_malignant: 'Sınır vaka, model kararsız',
  second_benign_ambl: 'İyi huylu lezyon (AMBL)',
  second_malignant_ambl: 'Kötü huylu lezyon (AMBL)',
}

export function Cases() {
  const [cases, setCases] = useState<CaseSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isSimple, isAdvanced } = useViewMode()

  useEffect(() => {
    api.listCases()
      .then((r) => {
        const sorted = [...r.cases].sort((a, b) => {
          const aSmart = a.selection_kind === 'smart' ? 0 : 1
          const bSmart = b.selection_kind === 'smart' ? 0 : 1
          return aSmart - bSmart
        })
        setCases(sorted)
        primeCaseIndex(sorted)

        const smart = sorted.filter((c) => c.selection_kind === 'smart')
        if (smart.length > 0 && typeof console.group === 'function') {
          console.groupCollapsed(
            `Sezi · ${smart.length} öne çıkan vaka (sebep listesi)`,
          )
          console.table(
            smart.map((c) => ({
              id: c.id,
              kohort: c.cohort,
              etiket: c.label === 'Malignant' ? 'kötü huylu'
                    : c.label === 'Benign' ? 'iyi huylu' : '?',
              kategori: c.demo_role ?? '',
              sebep: c.selection_reason ?? '',
            })),
          )
          console.groupEnd()
        }
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <p className="text-text-dim">Vakalar yükleniyor…</p>
  if (error) return <p className="text-malignant">Hata: {error}</p>

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-semibold mb-1 text-text">Demo vakalar</h1>
      <p className="text-text-dim text-sm mb-2 max-w-3xl leading-relaxed">
        Galerinin ilk yarısı modelin farklı davranışlarını göstermek için
        bilinçli olarak seçilmiş vakalardır: belirgin sonuçlar, sınır vakalar,
        modellerin çeliştiği örnekler ve kohort kayması gibi. İkinci yarısı doğrulama
        kümesinden rastgele örneklerdir. Bir vakanın detayını görmek için kart
        üzerine tıklayın.
      </p>
      {isAdvanced && (
        <p className="text-text-dim text-xs mb-6 max-w-3xl leading-relaxed">
          Her kartta lezyonun bulunduğu <Term id="kohort">kohort</Term>, hacmi ve iki
          farklı modelin <Term id="iyi-huylu-kotu-huylu">kötü huylu</Term> olma olasılığı
          gösterilir. İki model arasındaki farkın anlamı için{' '}
          <Link to="/hakkinda#iki-model" className="text-accent">Hakkında sayfasındaki
          açıklamaya</Link> bakabilirsiniz.
        </p>
      )}
      {isSimple && (
        <p className="text-text-dim text-xs mb-6 max-w-3xl leading-relaxed">
          Her kartta vakanın gerçek etiketi, modelin tahmini ve tahminin doğru
          olup olmadığı yer alır. Daha fazla teknik ayrıntı görmek için sağ
          üstten <span className="text-text font-medium">İleri</span> görünümüne
          geçebilirsiniz.
        </p>
      )}
      {cases.length === 0 ? (
        <p className="text-text-dim">Henüz görüntülenecek bir demo vaka yok.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {cases.map((c, i) => (
            <CaseCard key={c.id} c={c} number={i + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

function CaseCard({ c, number }: { c: CaseSummary; number: number }) {
  const { isSimple, isAdvanced } = useViewMode()
  const role = c.demo_role ? ROLE_LABEL[c.demo_role] || c.demo_role : null
  const isMalignant = c.label === 'Malignant'
  const labelCls = isMalignant ? 'text-malignant' : 'text-benign'
  const labelText = isMalignant ? 'Kötü huylu' : 'İyi huylu'
  const view = buildPredictionView(c)
  const correct = correctness(c, view)
  const verdictCls =
    view.verdict === 'malignant' ? 'text-malignant'
    : view.verdict === 'benign' ? 'text-benign'
    : 'text-warn'
  const confPct = Math.round(view.confidence * 100)

  return (
    <Link
      to={`/cases/${encodeURIComponent(c.id)}`}
      className="block border border-border rounded overflow-hidden bg-surface no-underline hover:border-accent transition-colors"
    >
      <div className="aspect-square bg-black overflow-hidden relative">
        <img
          src={`/api/cases/${encodeURIComponent(c.id)}/artifact/thumbnail.png`}
          alt={`Vaka #${number}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {correct !== 'unknown' && (
          <div className="absolute top-1.5 right-1.5">
            <CorrectnessBadge correctness={correct} iconOnly />
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between mb-1 gap-2">
          <span className="text-text font-medium text-sm truncate">
            Vaka #{number}
          </span>
          {c.label !== 'Unknown' && (
            <span className={`text-[10px] uppercase tracking-wider whitespace-nowrap ${labelCls}`}>
              {labelText}
            </span>
          )}
        </div>
        {isAdvanced && (
          <div className="text-xs text-text-dim mb-2">
            {c.cohort} · {c.lesion_volume_ml.toFixed(2)} mL
          </div>
        )}
        {isSimple && (
          <div className="text-xs text-text-dim mb-2">
            {c.lesion_volume_ml.toFixed(2)} mL
          </div>
        )}
        {isAdvanced && role && (
          <div className="text-xs text-text-dim border-t border-border pt-2 mt-2">
            {role}
          </div>
        )}
        {c.mask_kind === 'dual' && typeof c.dice_matched === 'number' && (
          <div className="text-xs mt-2 border-t border-border pt-2 flex items-baseline justify-between gap-2">
            <span className="text-text-dim">Segmentasyon:</span>
            <span className="text-accent font-medium">%{Math.round(c.dice_matched * 100)}</span>
          </div>
        )}
        <div className="text-xs mt-2 border-t border-border pt-2 flex items-baseline justify-between gap-2">
          <span className="text-text-dim">Modelin tahmini:</span>
          <span className={`${verdictCls} font-medium text-right truncate`}>
            {view.verdict === 'unsure'
              ? 'Emin değil'
              : `${VERDICT_LABEL[view.verdict]} · %${confPct}`}
          </span>
        </div>
        {isAdvanced && (
          <div className="text-[11px] text-text-dim font-mono mt-1.5 grid grid-cols-2 gap-2">
            <span>AMBL %{Math.round(c.p_malignant_v4v2 * 100)}</span>
            <span>Kombine %{Math.round(c.p_malignant_v16 * 100)}</span>
          </div>
        )}
      </div>
    </Link>
  )
}
